import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { CompanyDetailedDto, CompanyDetailedDtoQueryResultDto } from 'models/dtoTypes';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { CompanySearchV2 } from 'services/openapiClient';
import { CurrentUser } from 'auth';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { countryOptions } from 'utils/countries';
import { CompanyDetailedDtoWithSelector } from 'kosmos/models';
import generateNonce from 'utils/generateNonce';
import KosmosCompanyResultWithSmartGovernance from 'kosmos/components/KosmosCompanyResultWithSmartGovernance';

const PREFIX = 'CompanyTradeSunSearchWidget';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  red: `${PREFIX}-red`,
  green: `${PREFIX}-green`,
  appBar: `${PREFIX}-appBar`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  box: `${PREFIX}-box`,
  iconButton: `${PREFIX}-iconButton`,
  divider: `${PREFIX}-divider`,
  loadingBox: `${PREFIX}-loadingBox`,
  resultContainer: `${PREFIX}-resultContainer`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    width: '100%',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    '& > *': {
      flex: 1,
    },
  },

  [`& .${classes.red}`]: {
    backgroundColor: red,
  },

  [`& .${classes.green}`]: {
    backgroundColor: green,
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
  },

  [`& .${classes.contentContainer}`]: {
    width: '100%',
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    flex: 1,
    textAlign: 'left',
    padding: theme.spacing(1),
    paddingLeft: 0,
    overflowX: 'hidden',
  },

  [`& .${classes.title}`]: {
    lineHeight: 1,
  },

  [`& .${classes.box}`]: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    minWidth: 800,
    '& > *': {
      marginRight: 15,
    },
  },

  [`& .${classes.iconButton}`]: {
    padding: 0,
    height: 40,
    width: 120,
    fontSize: 16,
  },

  [`& .${classes.divider}`]: {
    height: 28,
    margin: 4,
  },
  [`& .${classes.loadingBox}`]: {
    width: '100%',
  },

  [`& .${classes.resultContainer}`]: {
    marginTop: 20,
    marginBottom: 20,
    '& .pagination': {
      marginBottom: 110,
    },
  },
}));

const red = '#FF75A8';
const green = '#26C9A8';

export interface IMessage {
  message?: string;
  severity?: 'error' | 'info';
}

export interface ICompanySearchResult {
  loading: boolean;
  CompaniesWithPageMeta?: CompanyDetailedDtoQueryResultDto;
  messages: IMessage[];
}

interface Props {
  title?: string;
  onShowOutOfCreditsNotice: () => void;
  onSelectCompany?: (id: string) => void;
  onSelectSgCompany?: (id: string) => void;
}

interface ICountryOption {
  label: string;
  alpha3: string;
}

export default function CompanyTradesunSearchWidget(props: Props): JSX.Element {
  const { t } = useTranslation('default');

  const EMPTY_COUNTRY = {
    label: t('select-country'),
    alpha3: '',
  };

  const currentUser = CurrentUser.getInstance();
  const intervalTimer = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // const POLL_INTERVAL = 60 * 1000;
  // const MIN_POLL_INTERVAL = 15 * 1000;
  const {
    title = t('company-search'),
    onSelectCompany,
    onSelectSgCompany,
    onShowOutOfCreditsNotice,
  } = props;

  const [result, setResult] = React.useState<ICompanySearchResult>({
    loading: false,
    CompaniesWithPageMeta: undefined,
    messages: [],
  });
  const [countries, setCountries] = useState<ICountryOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<ICountryOption>(EMPTY_COUNTRY);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyNumber, setCompanyNumber] = useState<string>('');
  const [addressLine, setAddressLine] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [companiesList, setCompaniesList] = useState<CompanyDetailedDtoWithSelector[]>([]);

  const fetchCountries = () => {
    const options = countryOptions();
    let formattedOptions = options.map((item) => ({
      label: item.label || '',
      alpha3: item.alias || '',
    }));
    formattedOptions = formattedOptions.filter((a) => a.alpha3 !== 'world');
    formattedOptions.unshift(EMPTY_COUNTRY);
    const selected = formattedOptions.find((o) => o.alpha3 === currentUser.getScope('country'));
    if (selected) {
      setSelectedCountry(selected);
    }
    setCountries(formattedOptions);
  };

  useEffect(() => {
    fetchCountries();
    return () => stopPoll();
  }, []);

  const handleChangeCompanyName = (e: { target: { name: string; value: string } }) => {
    setCompanyName(e.target.value);
  };

  const handleChangeCompanyNumber = (e: { target: { name: string; value: string } }) => {
    setCompanyNumber(e.target.value);
  };

  const handleChangeAddressLine = (e: { target: { name: string; value: string } }) => {
    setAddressLine(e.target.value);
  };

  const handleChangePostalCode = (e: { target: { name: string; value: string } }) => {
    setPostalCode(e.target.value);
  };

  const validateInputFields = () => {
    if (companyNumber === '' && (companyName === '' || companyName.length < 3)) {
      return false;
    }
    const countryCode = selectedCountry.alpha3;
    if (countryCode === '') {
      return false;
    }
    return true;
  };

  const fetchCompanies = async (flag = false) => {
    const isValid = validateInputFields();
    if (!isValid) {
      setResult({
        loading: false,
        messages: [
          {
            message: t('need-company-namecompany-number-and-company-country'),
            severity: 'info',
          },
        ],
      });
      return;
    }
    setDataLoading(true);
    if (flag) {
      setResult({
        loading: true,
        messages: [],
      });
    }
    try {
      const countryCode = selectedCountry.alpha3;
      const companiesWithPageMeta = await CompanySearchV2({
        companyName,
        companyNumber,
        countryCode,
        addressLine,
        postalCode,
      });
      const hasCompany = companiesWithPageMeta?.items && companiesWithPageMeta.items.length > 0;
      const messages = [];
      if (!hasCompany) {
        messages.push({
          message: t('no-record-found'),
          severity: 'info' as 'info' | 'error',
        });
      } else {
        const tempCompanies: CompanyDetailedDtoWithSelector[] = [];
        companiesWithPageMeta?.items?.forEach((a: CompanyDetailedDto) => {
          const tempCompany = JSON.parse(JSON.stringify(a));
          tempCompany['uniqueIdSelector'] = generateNonce();
          tempCompanies.push(tempCompany);
        });
        setCompaniesList(tempCompanies);
      }
      setResult({
        loading: false,
        CompaniesWithPageMeta: companiesWithPageMeta,
        messages: messages,
      });
      // stopPoll();
      setDataLoading(false);
    } catch (e) {
      setResult({
        loading: false,
        CompaniesWithPageMeta: undefined,
        messages: [
          {
            message: e instanceof Error ? e.message : String(e),
            severity: 'error',
          },
        ],
      });
      stopPoll();
      setDataLoading(false);
    }
  };

  const stopPoll = () => {
    const timer = intervalTimer.current;
    if (timer) {
      clearInterval(timer);
      intervalTimer.current = null;
    }
    const timer2 = timeoutTimer.current;
    if (timer2) {
      clearTimeout(timer2);
      timeoutTimer.current = null;
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCompanies(true);
  };

  const handleReset = () => {
    stopPoll();
    setCompanyName('');
    setAddressLine('');
    setPostalCode('');
    setCompanyNumber('');
    setSelectedCountry(EMPTY_COUNTRY);
    setDataLoading(false);
    setResult({
      loading: false,
      messages: [
        {
          message: t('need-company-namecompany-number-and-company-country'),
          severity: 'info',
        },
      ],
    });
  };

  const handleChangeCountry = (event: unknown, newValue: ICountryOption | null) => {
    const selected = newValue || EMPTY_COUNTRY;
    CurrentUser.getInstance().setScope('country', selected.alpha3);
    setSelectedCountry(selected);
  };

  const renderLoading = () => {
    return (
      <Box className={classes.loadingBox}>
        <Skeleton height={100} />
        <Skeleton height={100} />
        <Skeleton height={100} />
      </Box>
    );
  };

  const renderResultMessage = () => {
    const { messages } = result;
    const formattedMessages = [...messages];
    if (formattedMessages?.length) {
      return (
        <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
          {formattedMessages.map((m, i) => (
            <Alert key={i} severity={m.severity || 'error'}>
              {m.message}
            </Alert>
          ))}
        </Stack>
      );
    } else {
      return <></>;
    }
  };

  const renderResult = () => {
    const { loading } = result;
    if (loading) return renderLoading();
    else if (companiesList && companiesList.length > 0) {
      return (
        <KosmosCompanyResultWithSmartGovernance
          count={companiesList.length}
          companies={companiesList}
          onOutOfCredits={onShowOutOfCreditsNotice}
          onSelectCompany={onSelectCompany}
          onSelectSgCompany={onSelectSgCompany}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Root className={classes.root}>
      <main className={classes.contentContainer}>
        <div className={classes.content}>
          {title && <h2 className={classes.title}>{title}</h2>}
          <Box component="form" onSubmit={handleSearch}>
            <Box className={classes.box}>
              <TextField
                value={companyName}
                disabled={dataLoading}
                onChange={handleChangeCompanyName}
                placeholder={t('company-name')}
                inputProps={{ 'aria-label': t('company-name') }}
                sx={{ width: 200 }}
                size="small"
              />
              <TextField
                value={companyNumber}
                disabled={dataLoading}
                onChange={handleChangeCompanyNumber}
                placeholder={t('search-by-external-identifier')}
                inputProps={{ 'aria-label': t('company-number') }}
                sx={{ width: 200 }}
                size="small"
              />
              <Autocomplete
                disabled={countries.length < 1 || dataLoading}
                id=""
                value={selectedCountry}
                onChange={handleChangeCountry}
                options={countries}
                sx={{ width: 200 }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label={t('country')}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <TextField
                value={addressLine}
                disabled={dataLoading}
                onChange={handleChangeAddressLine}
                placeholder={t('company-address')}
                inputProps={{ 'aria-label': t('company-address') }}
                sx={{ width: 200 }}
                size="small"
              />
              <TextField
                value={postalCode}
                disabled={dataLoading}
                onChange={handleChangePostalCode}
                placeholder={t('postalzip-code')}
                inputProps={{ 'aria-label': t('postalzip-code') }}
                sx={{ width: 200 }}
                size="small"
              />{' '}
              <Button
                type="submit"
                disabled={dataLoading}
                onClick={handleSearch}
                className={classes.iconButton}
                aria-label="search"
                variant="contained"
                color="primary"
                endIcon={<SearchIcon />}
              >
                {t('search')}
              </Button>
              <Button
                disabled={dataLoading}
                onClick={handleReset}
                className={classes.iconButton}
                aria-label="reset"
                variant="contained"
                color="primary"
                endIcon={<CloseIcon />}
              >
                {t('reset')}
              </Button>
            </Box>
          </Box>
        </div>
        <div className={classes.resultContainer}>
          {renderResultMessage()}
          {renderResult()}
        </div>
      </main>
    </Root>
  );
}
